import { useAccountStore } from './account';
import { AxiosError } from 'axios';
import { Dialog } from 'quasar';
import { defineStore } from 'pinia';
import { api } from 'services/api';
import { showChangeActionDialog } from 'services/notification';
import User from '@models/User';
import { useDayjs } from 'composables/dayjs';
import ImpersonationDialog from 'components/ImpersonationDialog.vue';

type ApiUserError = {
  error: string;
  key: string;
};

export const useUserStore = defineStore({
  id: 'user',
  state: () => ({
    editingUserEmailKey: '',
    inProgress: {} as { [key: string]: boolean },
    users: {} as { [key: string]: User },
  }),
  getters: {
    usersById(state) {
      const usersById: { [id: string]: User } = {};
      Object.keys(state.users).forEach((key) => {
        const user = state.users[key];
        if (user.id) {
          usersById[user.id] = user;
        }
      });
      return usersById;
    },
  },
  actions: {
    async changeZauthPassword(key: string, password: string) {
      let result = { success: true } as { success: boolean; error?: string; historyId?: number; status?: number };

      try {
        // If this works, password has changed
        const { data } = await api.patch<{ success: boolean; error: string; history_id: number }>(
          `/user/${key}/zauth`,
          { password }
        );
        result.historyId = data.history_id;
      } catch (error) {
        const responseError = (<{ error: string }>(<AxiosError>error).response?.data).error;
        result = {
          success: false,
          error: responseError || (<Error>error).message,
          status: (<AxiosError>error).response?.status || 500,
        };
      }

      if (result.success) {
        const user = this.users[key];
        showChangeActionDialog('Zauth password has been successfully changed.', {
          accountKey: user.accountKey,
          historyId: <number>result.historyId,
          icon: 'key',
          seamless: false,
        });
      }

      return result;
    },
    getUser(id: string) {
      return this.getUsers([id]);
    },
    async getUsers(keys: string[]) {
      await Promise.all(
        keys.map(async (key) => {
          const { users, inProgress } = this;
          if (users[key] || inProgress[key]) {
            return;
          }

          try {
            this.inProgress[key] = true;
            const response = await api.get<ApiUser>(`/user/${key}`);
            this.insertUser(response.data);
          } catch (error) {
            this.insertUser({ key, error: (<Error>error).message });
          }

          delete this.inProgress[key];
        })
      );
    },
    async getZauthProfile(key: string) {
      let profile: ZauthProfile | { error: string; status: number };

      try {
        const { data } = await api.get<ZauthProfile>(`/user/${key}/zauth`);
        profile = data;
      } catch (error) {
        profile = { error: (<Error>error).message, status: (<AxiosError>error).response?.status || 500 };
      }

      return profile;
    },
    impersonate(key: string) {
      Dialog.create({
        component: ImpersonationDialog,
        componentProps: {
          userKey: key,
        },
      });
    },
    async impersonatePrecheck(key: string) {
      const { data } = await api.get<{ success: boolean; error?: string }>(`/user/${key}/impersonate/precheck`);
      if (!data.success) {
        throw new Error(data.error);
      }
      return true;
    },
    insertUser(userData: ApiUser | ApiUserError) {
      const { dayjs } = useDayjs();
      let user;

      if ((<ApiUser>userData).id) {
        const apiUser = <ApiUser>userData;
        user = {
          accountKey: apiUser.account_key,
          accountRole: apiUser.account_role,
          active: apiUser.active,
          created: dayjs(apiUser.created).toDate(),
          email: apiUser.email,
          firstName: apiUser.first_name,
          id: apiUser.id,
          key: apiUser.key,
          lastName: apiUser.last_name,
        };
      } else {
        const apiUserError = <ApiUserError>userData;
        user = {
          error: apiUserError.error,
          key: apiUserError.key,
        };
      }

      this.users[user.key] = <User>user;
    },
    async updateEmail(key: string, email: string) {
      const { currentAccount } = useAccountStore();
      let result = { success: true } as { success: boolean; error?: string; historyId?: number; status?: number };

      try {
        const { data } = await api.patch<{ success: boolean; error: string; history_id: number }>(
          `/user/${key}?accountKey=${<string>currentAccount}`,
          {
            email,
          }
        );
        result.historyId = data.history_id;
      } catch (error) {
        const responseError = (<{ error: string }>(<AxiosError>error).response?.data).error;
        result = {
          success: false,
          error: responseError || (<Error>error).message,
          status: (<AxiosError>error).response?.status || 500,
        };
      }

      if (result.success) {
        this.users[key].email = email;
        const user = this.users[key];
        showChangeActionDialog(`User email has been changed to: ${email}`, {
          accountKey: user.accountKey,
          historyId: <number>result.historyId,
          icon: 'mail',
        });
      }

      return result;
    },
  },
});
